import React from 'react';
import { ArrowLeft, Languages, Printer, EyeOff, Settings2, RefreshCw, Eye } from 'lucide-react';
import { PrintDialog } from './PrintDialog';

interface AnonymizationOptions {
  operation: boolean;
  team: boolean;
  names: boolean;
}

interface ReportHeaderProps {
  onBackToForm: () => void;
  onTranslate: () => void;
  onPrint: (options: { showCover: boolean }) => void;
  onToggleAttachments?: () => void;
  onRegenerateReport: () => void;
  onAnonymizationChange: (options: AnonymizationOptions) => void;
  anonymizationOptions: AnonymizationOptions;
  hasAttachments: boolean;
  hideAttachments: boolean;
  isTranslating: boolean;
  isEnglish: boolean;
  isFullyLoaded: boolean;
  isRegenerating: boolean;
  taskName: string;
}

export const ReportHeader: React.FC<ReportHeaderProps> = ({
  onBackToForm,
  onTranslate,
  onPrint,
  onToggleAttachments,
  onRegenerateReport,
  onAnonymizationChange,
  anonymizationOptions,
  hasAttachments,
  hideAttachments,
  isTranslating,
  isEnglish,
  isFullyLoaded,
  isRegenerating,
  taskName,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isAnonymizeOpen, setIsAnonymizeOpen] = React.useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = React.useState(false);

  const handlePrint = (options: { showCover: boolean }) => {
    onPrint(options);
    setIsPrintDialogOpen(false);
    setIsMenuOpen(false);
  };

  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
        setIsAnonymizeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAnyAnonymizationEnabled = Object.values(anonymizationOptions).some(value => value);

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 print:hidden">
        <button
          onClick={onBackToForm}
          className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isEnglish ? 'Return' : 'Retornar'}
        </button>
        
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            onClick={onRegenerateReport}
            disabled={isRegenerating}
            className="flex items-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1.5 rounded-md transition-colors text-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
            <span className="hidden md:inline">
              {isRegenerating 
                ? (isEnglish ? 'Regenerating...' : 'Regenerando...')
                : (isEnglish ? 'Regenerate Report' : 'Gerar Novamente')
              }
            </span>
          </button>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors text-sm ${
                isAnyAnonymizationEnabled 
                  ? 'bg-purple-100 hover:bg-purple-200 text-purple-700'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <Settings2 className="w-4 h-4" />
              <span className="hidden md:inline">
                {isEnglish ? 'Manage Data' : 'Manipular Dados'}
              </span>
              {isAnyAnonymizationEnabled && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-200 text-purple-800 rounded-full">
                  {isEnglish ? 'Anonymized' : 'Anon.'}
                </span>
              )}
            </button>

            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                <div className="py-1">
                  {hasAttachments && onToggleAttachments && (
                    <button
                      onClick={() => {
                        onToggleAttachments();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {hideAttachments ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                      {isEnglish ? (hideAttachments ? 'Show Attachments' : 'Hide Attachments') : (hideAttachments ? 'Mostrar Anexos' : 'Ocultar Anexos')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      onTranslate();
                      setIsMenuOpen(false);
                    }}
                    disabled={isTranslating}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    <Languages className="w-4 h-4 mr-2" />
                    {isTranslating ? (isEnglish ? 'Translating...' : 'Traduzindo...') : isEnglish ? 'Ver em Português' : 'View in English'}
                  </button>
                  {isFullyLoaded && (
                    <button
                      onClick={() => setIsAnonymizeOpen(!isAnonymizeOpen)}
                      className={`flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-gray-100 ${
                        isAnyAnonymizationEnabled ? 'text-purple-700' : 'text-gray-700'
                      }`}
                    >
                      <div className="flex items-center">
                        {isAnyAnonymizationEnabled ? (
                          <EyeOff className="w-4 h-4 mr-2" />
                        ) : (
                          <Eye className="w-4 h-4 mr-2" />
                        )}
                        {isEnglish ? 'Anonymize Data' : 'Anonimizar Dados'}
                      </div>
                      {isAnyAnonymizationEnabled && (
                        <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                          {Object.values(anonymizationOptions).filter(Boolean).length}
                        </span>
                      )}
                    </button>
                  )}
                </div>

                {isAnonymizeOpen && isFullyLoaded && (
                  <div className="border-t py-2 bg-gray-50">
                    <div className="px-4 py-2">
                      <h3 className="text-sm font-medium text-gray-900 mb-3">
                        {isEnglish ? 'Choose data to anonymize:' : 'Escolha os dados para anonimizar:'}
                      </h3>
                      <div className="space-y-2">
                        <label className="flex items-center p-2 hover:bg-gray-100 rounded transition-colors">
                          <input
                            type="checkbox"
                            checked={anonymizationOptions.operation}
                            onChange={() => onAnonymizationChange({
                              ...anonymizationOptions,
                              operation: !anonymizationOptions.operation
                            })}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {isEnglish ? 'Operation' : 'Operação'}
                          </span>
                        </label>
                        <label className="flex items-center p-2 hover:bg-gray-100 rounded transition-colors">
                          <input
                            type="checkbox"
                            checked={anonymizationOptions.team}
                            onChange={() => onAnonymizationChange({
                              ...anonymizationOptions,
                              team: !anonymizationOptions.team
                            })}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {isEnglish ? 'Team' : 'Time'}
                          </span>
                        </label>
                        <label className="flex items-center p-2 hover:bg-gray-100 rounded transition-colors">
                          <input
                            type="checkbox"
                            checked={anonymizationOptions.names}
                            onChange={() => onAnonymizationChange({
                              ...anonymizationOptions,
                              names: !anonymizationOptions.names
                            })}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {isEnglish ? 'Names in Content' : 'Nomes nos Conteúdos'}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => setIsPrintDialogOpen(true)}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            title={isEnglish ? 'Print' : 'Imprimir'}
          >
            <Printer className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      <PrintDialog
        isOpen={isPrintDialogOpen}
        onClose={() => setIsPrintDialogOpen(false)}
        onConfirm={handlePrint}
        isEnglish={isEnglish}
      />
    </>
  );
};